import { CalculateNetSalePrice } from 'src/app/shared/services/financialTotals/financial-totals.service';
import { ICON } from '@core/util/icon';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { cloneDeep } from 'lodash';
import { InputComponent } from 'src/app/shared/atomic/atoms/input/input.component';
import { Component, Input } from '@angular/core';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { ItemizedFeeType, MatterFileType } from '@models/matter';
import { ItemizedFeeTypeDescription } from '@core/util/matter';
import { MatterDispatch } from '@state/matter';
import { TrustLedgerItem } from '@state/global/trust-ledger/trust_ledger.models';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { createId } from 'src/app/shared/core/util/file';

const Items: DropdownOption<ItemizedFeeType>[] = [
	{
		name: ItemizedFeeTypeDescription[ItemizedFeeType.WIRE_FEE],
		value: ItemizedFeeType.WIRE_FEE,
	},
	{
		name: ItemizedFeeTypeDescription[ItemizedFeeType.OTHER],
		value: ItemizedFeeType.OTHER,
	},
];

@Component({
	selector: 'app-sale-price-itemization',
	templateUrl: './sale-price-itemization.component.html',
	styleUrls: ['./sale-price-itemization.component.scss'],
	standalone: false,
})
export class SalePriceItemizationComponent {
	public ICON = ICON;
	public items = Items;
	public _trustLedgerItem: TrustLedgerItem;
	public netSalePriceAmount: number;
	public isEditDescriptionList: boolean[] = [];
	public showDropdownOptions = false;
	public hasItemizedFees = false;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterFileType) matterFileType: MatterFileType;

	@Input() isDrawerOpen = false;

	@Input() set trustLedgerItem(trustLedgerItem: TrustLedgerItem) {
		this._trustLedgerItem = cloneDeep(trustLedgerItem);
		if (trustLedgerItem) {
			this.netSalePriceAmount = CalculateNetSalePrice(this._trustLedgerItem);
		}
		this.isEditDescriptionList = [];
		this._trustLedgerItem?.itemizedFees?.forEach((itemizedFee) => {
			this.isEditDescriptionList.push(!itemizedFee?.description);
		});
		this.hasItemizedFees = !!this._trustLedgerItem?.itemizedFees?.length;
	}

	constructor(
		private matterDispatch: MatterDispatch,
		private analytics: AnalyticsService,
	) {}

	itemsTrackBy(index: number) {
		return index;
	}

	toggleDropdown() {
		if (this.isDrawerOpen) {
			this.analytics.logEvent(
				AnalyticsEvent.SALE_PRICE_ITEM_DRAWER_ADD_ITEM_BUTTON_CLICK,
			);
		}
		this.showDropdownOptions = !this.showDropdownOptions;
	}

	hideDropdown() {
		this.showDropdownOptions = false;
	}

	onSelect(item: DropdownResult<DropdownOption<ItemizedFeeType>>) {
		this.addItem(item.result.value);
	}

	removeItem(index: number) {
		const { itemizedFees } = this._trustLedgerItem;
		itemizedFees.splice(index, 1);
		const netAmount = CalculateNetSalePrice(this._trustLedgerItem);
		this.netSalePriceAmount = netAmount;
		if (this.matterFileType === MatterFileType.SALE) {
			this.matterDispatch.UpdateReceipt(
				this.matterId,
				this._trustLedgerItem.id,
				{
					itemizedFees: itemizedFees,
				},
			);
		} else if (this.matterFileType === MatterFileType.PURCHASE) {
			this.matterDispatch.UpdatePayment(
				this.matterId,
				this._trustLedgerItem.id,
				{
					itemizedFees: itemizedFees,
				},
			);
		}
		this.hasItemizedFees = !!this._trustLedgerItem?.itemizedFees?.length;
	}

	updateItemizedFees() {
		const { itemizedFees } = this._trustLedgerItem;
		const netAmount = CalculateNetSalePrice(this._trustLedgerItem);
		this.netSalePriceAmount = netAmount;
		if (this.matterFileType === MatterFileType.SALE) {
			this.matterDispatch.UpdateReceipt(
				this.matterId,
				this._trustLedgerItem.id,
				{
					itemizedFees: itemizedFees,
				},
			);
		} else if (this.matterFileType === MatterFileType.PURCHASE) {
			this.matterDispatch.UpdatePayment(
				this.matterId,
				this._trustLedgerItem.id,
				{
					itemizedFees: itemizedFees,
				},
			);
		}
		this.hasItemizedFees = !!this._trustLedgerItem?.itemizedFees?.length;
	}

	onDescriptionSave(index: number, amountInput: InputComponent) {
		const { itemizedFees } = this._trustLedgerItem;
		itemizedFees[index].description.trim();
		this.updateItemizedFees();
		setTimeout(() => {
			amountInput.inputElementRef.nativeElement.focus();
		});
	}

	getPlaceholder(type: ItemizedFeeType) {
		return ItemizedFeeTypeDescription[type];
	}

	addItem(type: ItemizedFeeType): void {
		if (this.isDrawerOpen && type === ItemizedFeeType.WIRE_FEE) {
			this.analytics.logEvent(
				AnalyticsEvent.SALE_PRICE_ITEM_DRAWER_ADD_WIRE_FEE_BUTTON_CLICK,
			);
		} else if (this.isDrawerOpen && type === ItemizedFeeType.OTHER) {
			this.analytics.logEvent(
				AnalyticsEvent.SALE_PRICE_ITEM_DRAWER_ADD_CUSTOM_FEE_BUTTON_CLICK,
			);
		}
		const { itemizedFees } = this._trustLedgerItem;
		const newItem = {
			itemizedFees: [
				...itemizedFees,
				{
					id: createId(),
					description: [ItemizedFeeType.OTHER].includes(type)
						? ''
						: ItemizedFeeTypeDescription[type],
					amount: 0,
					type,
				},
			],
		};
		if (this.matterFileType === MatterFileType.SALE) {
			this.matterDispatch.UpdateReceipt(
				this.matterId,
				this._trustLedgerItem.id,
				newItem,
			);
		} else if (this.matterFileType === MatterFileType.PURCHASE) {
			this.matterDispatch.UpdatePayment(
				this.matterId,
				this._trustLedgerItem.id,
				newItem,
			);
		}
		this.hasItemizedFees = !!this._trustLedgerItem?.itemizedFees?.length;
	}
}
